/**
 * RailTheme — the appearance control, in the rail footer rather than the top bar.
 * Expanded: a two-segment Light | Dark switch. Collapsed: one icon button that
 * toggles. ThemeContext is two-state, so there is no 'System' option to offer —
 * adding one means teaching ThemeContext to follow prefers-color-scheme.
 */

import React from 'react'
import clsx from 'clsx'
import Sym from './Sym'
import { useTheme } from '../contexts/ThemeContext'

const MODES = [
  { id: 'light', label: 'Light', icon: 'light_mode' },
  { id: 'dark', label: 'Dark', icon: 'dark_mode' },
] as const

export default function RailTheme({ open }: { open: boolean }): React.JSX.Element {
  const { theme, setTheme, toggleTheme } = useTheme()

  if (!open) {
    return (
      <button
        onClick={toggleTheme}
        aria-label={`Appearance: ${theme}. Switch to ${theme === 'dark' ? 'light' : 'dark'}`}
        className="cx-navi mx-auto flex h-[34px] w-[34px] items-center justify-center rounded-[9px] text-mut hover:bg-panel2"
      >
        <Sym name={theme === 'dark' ? 'dark_mode' : 'light_mode'} size={19} />
      </button>
    )
  }

  return (
    <div role="group" aria-label="Appearance" className="flex w-full gap-[3px] rounded-[10px] border border-line p-[3px]">
      {MODES.map((m) => (
        <button
          key={m.id}
          onClick={() => setTheme(m.id)}
          aria-pressed={theme === m.id}
          className={clsx(
            'cx-navi flex flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-[7px] py-1.5 text-[12px]',
            theme === m.id ? 'bg-panel2 font-semibold text-ink' : 'font-medium text-mut'
          )}
        >
          <Sym name={m.icon} size={15} weight={400} />
          {m.label}
        </button>
      ))}
    </div>
  )
}
